import { NavLink } from 'react-router-dom'
import {
  Home, User, CalendarDays, ClipboardEdit, BarChart3, Library, Award,
  LayoutGrid, FileText, Building2, Search, FileBarChart, ExternalLink,
  Megaphone, ScrollText, ShieldCheck, Database,
  type LucideIcon,
} from 'lucide-react'
import type { CurrentUser } from '@/features/auth/api'

interface MenuItem {
  to: string
  label: string
  Icon: LucideIcon
  external?: boolean
  exact?: boolean
}

const USER_MENU: MenuItem[] = [
  { to: '/',             label: 'หน้าหลัก',     Icon: Home,         exact: true },
  { to: '/profile',      label: 'ข้อมูลส่วนตัว',  Icon: User },
  { to: '/schedule',     label: 'ตารางสอบ',     Icon: CalendarDays },
  { to: '/quiz',         label: 'ห้องสอบ',       Icon: ClipboardEdit },
  { to: '/quiz-result',  label: 'ผลการสอบ',     Icon: BarChart3 },
  { to: 'https://dev.rpca.ac.th/index_pdf', label: 'คลังความรู้', Icon: Library, external: true },
  { to: '/certificate',  label: 'ใบรับรองผล',    Icon: Award },
]

const ADMIN_MENU: MenuItem[] = [
  { to: '/admin',                label: 'หน้าหลัก',           Icon: LayoutGrid,  exact: true },
  { to: '/admin/exams',          label: 'การสอบ',              Icon: FileText },
  { to: '/admin/question-library', label: 'คลังข้อสอบ',         Icon: Database },
  { to: '/admin/exam-fields',    label: 'หน่วยจัดการสอบ',       Icon: Building2 },
  { to: '/admin/announcements',  label: 'ประกาศหน้า Login',     Icon: Megaphone },
  { to: '/admin/users',          label: 'บริหารจัดการผู้สมัคร', Icon: Search },
  { to: '/admin/admins',         label: 'ผู้ดูแลระบบ',          Icon: ShieldCheck },
  { to: '/admin/reports',        label: 'รายงานผลสอบ',         Icon: FileBarChart },
  { to: '/admin/audit-log',      label: 'Audit Log',           Icon: ScrollText },
]

export function Sidebar({ user, onClose }: { user: CurrentUser; onClose?: () => void }) {
  const menu = user.type === 'admin' ? ADMIN_MENU : USER_MENU
  return (
    <aside className="w-64 shrink-0 bg-white h-screen flex flex-col border-r border-ink-rule">
      {/* brand row — minimal wordmark */}
      <div className="px-5 py-4 flex items-center gap-3 border-b border-ink-rule">
        <div className="w-9 h-9 rounded-md bg-brand grid place-items-center text-white text-sm font-bold tracking-tight">
          R
        </div>
        <div className="leading-tight">
          <div className="text-sm font-semibold text-ink">RPCA</div>
          <div className="text-[11px] text-ink-mute">ระบบทดสอบความรู้</div>
        </div>
      </div>

      {/* user identity */}
      <div className="px-5 py-3.5 border-b border-ink-rule">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-paper-deep grid place-items-center text-ink-soft">
            <User size={16} strokeWidth={1.5} />
          </div>
          <div className="min-w-0 leading-tight flex-1">
            <div className="text-sm font-medium text-ink truncate" title={`${user.fname} ${user.lname}`}>
              {user.fname} {user.lname}
            </div>
            <div className="text-[11px] text-ink-mute mt-0.5">
              {user.type === 'admin' ? 'ผู้ดูแลระบบ' : 'ผู้สมัครสอบ'}
            </div>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-2 px-2">
        {menu.map((item) => {
          const I = item.Icon
          if (item.external) {
            return (
              <a
                key={item.to}
                href={item.to}
                target="_blank"
                rel="noreferrer"
                onClick={onClose}
                className="group flex items-center gap-3 px-3 py-2 text-sm text-ink-soft hover:bg-paper-deep hover:text-ink rounded-md"
              >
                <I size={16} strokeWidth={1.6} className="shrink-0 text-ink-mute group-hover:text-ink" />
                <span className="flex-1">{item.label}</span>
                <ExternalLink size={12} className="text-ink-mute" />
              </a>
            )
          }
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.exact}
              onClick={onClose}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2 text-sm rounded-md transition ${
                  isActive
                    ? 'bg-brand/10 text-brand font-medium'
                    : 'text-ink-soft hover:bg-paper-deep hover:text-ink'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <I
                    size={16}
                    strokeWidth={1.6}
                    className={`shrink-0 ${isActive ? 'text-brand' : 'text-ink-mute group-hover:text-ink'}`}
                  />
                  <span className="flex-1">{item.label}</span>
                </>
              )}
            </NavLink>
          )
        })}
      </nav>

      <div className="px-5 py-3 border-t border-ink-rule text-[11px] text-ink-mute font-mono">
        v0.1.0
      </div>
    </aside>
  )
}
